import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip
} from '@mui/material';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { styled } from '@mui/material/styles';
import { Global } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import IELTSBottomNavigation from './IELTSBottomNavigation';
import IELTSHeader from './IELTSHeader';
import IELTSBandScore from './IELTSBandScore';
import ReadingResultsFrame from './ReadingResultsFrame';
import { Base_URL } from './constants/api';
import { getStoredLaunchGrant } from '../../utils/mockTestLinks';
import {
  ieltsPassageGlobalStyles,
  ieltsPassageInlineStyles,
  ieltsReadingPassageStyles,
  ieltsReadingPassageInlineStyles,
} from './styles/passageStyles';
import {
  extractBlankNumberMappingAndCleanHtml,
  processPassageText,
  findQuestionNumberFromSiblings,
  sanitizeOfficialExplanationHtml,
  scrollToOfficialAnswerHighlight,
} from './utils/passageHelpers';
import {
  READING_ANNOTATION_VERSION,
  composeReadingPassageHtml,
  getReadingSelectionOffsets,
  mergeReadingAnnotation,
  restoreReadingAnnotations,
} from './utils/readingAnnotations';
import {
  createQuestionNumberToDbIdMap,
  buildAnswersArray,
  checkSkippedQuestions,
} from './utils/questionHelpers';
import { hydrateMCQContainers } from './utils/mcqHydration';
import { hydrateDragDropFields } from './utils/dragDropHelpers';
import { expandQuestionRange } from './utils/mcqHelpers';
import { formatTime } from './utils/timeHelpers';
import { QuestionsContainer } from './styles/componentStyles';
import { markTFNInfoTables } from './utils/htmlTableMarkers';
import useIeltsAutosave from './hooks/useIeltsAutosave';
import { getObjectiveScoringMetadata } from './utils/objectiveScoringPayload';

const TestContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  height: '100vh',
  width: '100%',
  overflow: 'hidden',
}));


// Styled PassageContainer for reading tests (extends QuestionsContainer with paddingBottom)
const PassageContainer = styled(QuestionsContainer)(({ theme }) => ({
  paddingBottom: '77px',
}));


const DraggableDivider = styled(Box)(({ theme, dragging }) => ({
  width: '6px',
  cursor: 'col-resize',
  background: dragging ? '#C7B6FF' : '#E0E0E0',
  transition: 'background 0.2s',
  zIndex: 2,
  borderRadius: '4px',
  margin: '0 2px',
  alignSelf: 'stretch',
}));

// Split-pane: left = passage, right = questions. Both sides start balanced and can resize up to 80%.
const LEFT_WIDTH_DEFAULT = 50;
const LEFT_WIDTH_MIN = 20;
const LEFT_WIDTH_MAX = 80;

function getReadingPassageKey(section, partIndex, selectedSectionIndices) {
  const sectionId = section?.id ?? section?.section_id;
  if (sectionId !== undefined && sectionId !== null) return `section:${sectionId}`;
  return `index:${selectedSectionIndices?.[partIndex] ?? partIndex}`;
}

// Helper function for reading band calculation
function getReadingBand(score) {
  if (score >= 39) return 9;
  if (score >= 37) return 8.5;
  if (score >= 35) return 8;
  if (score >= 33) return 7.5;
  if (score >= 30) return 7;
  if (score >= 27) return 6.5;
  if (score >= 23) return 6;
  if (score >= 19) return 5.5;
  if (score >= 15) return 5;
  if (score >= 13) return 4.5;
  if (score >= 10) return 4;
  if (score >= 7) return 3.5;
  if (score >= 5) return 3;
  if (score >= 3) return 2.5;
  return 0;
}

const ReadingTestUI = ({ mode = 'practice', timeLimit, resultDuration, resultDurationLabel, teacherReviewContent, onComplete, sections = [], totalPartCount = sections.length, answer_sheet, testId, userId = 3, explanation, selectedSectionIndices, groupId, fullTestAttemptId, showSaveExit = false, apiResult: apiResultProp, onFullTestPause, restoreDraft = false, isSimulationMode = mode === 'simulation' }) => {
  const navigate = useNavigate();
  const [currentPart, setCurrentPart] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timer, setTimer] = useState({
    minutes: timeLimit === 'Unlimited' ? 'Unlimited' : (timeLimit || 60),
    seconds: 0
  });
  const isTestMode = mode === 'test' || mode === 'review';
  const isReviewMode = mode === 'review';
  
  const [isTimerRunning, setIsTimerRunning] = useState(!isReviewMode);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [leftWidth, setLeftWidth] = useState(LEFT_WIDTH_DEFAULT);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const passageRef = useRef(null);
  const highlightResetRef = useRef(null);
  const [cleanedPassageText, setCleanedPassageText] = useState('');
  const [blankNumbersInOrder, setBlankNumbersInOrder] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [tableBlankNumbersInOrder, setTableBlankNumbersInOrder] = useState([]);
  const [selectionValues, setSelectionValues] = useState({});
  const [blankIdToNumberMap, setBlankIdToNumberMap] = useState({});
  const [selectionIdToNumberMap, setSelectionIdToNumberMap] = useState({});
  const [selectionNumbersInOrder, setSelectionNumbersInOrder] = useState([]);
  const [tableSelectionNumbersInOrder, setTableSelectionNumbersInOrder] = useState([]);
  const [selectionNumbersFinal, setSelectionNumbersFinal] = useState([]);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const [allAnswers, setAllAnswers] = useState({});
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [showRedForUnanswered, setShowRedForUnanswered] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  const [testStartTime] = useState(() => new Date().toISOString().slice(0, 19).replace('T', ' '));
  const [testEndTime, setTestEndTime] = useState(null);
  const [readingAnnotations, setReadingAnnotations] = useState({
    version: READING_ANNOTATION_VERSION,
    passages: {},
  });
  const [readingSelection, setReadingSelection] = useState(null);
  const [activeReadingAnnotation, setActiveReadingAnnotation] = useState(null);
  const [readingNoteDraft, setReadingNoteDraft] = useState('');
  const [officialExplanation, setOfficialExplanation] = useState('');
  
  const testParts = [1, 2, 3];
  const currentSection = sections?.[currentPart - 1];
  const currentPassageHtml = currentSection?.passage_html || '';
  const currentPassageKey = getReadingPassageKey(currentSection, currentPart - 1, selectedSectionIndices);
  const currentReadingAnnotations = readingAnnotations.passages[currentPassageKey] || [];
  const currentOfficialHighlights = (isReviewMode || apiResult)
    ? (currentSection?.answer_highlights || currentSection?.answerHighlights || [])
    : [];
  const annotatedPassageHtml = useMemo(() => composeReadingPassageHtml(
    currentPassageHtml,
    currentReadingAnnotations,
    currentOfficialHighlights
  ), [currentOfficialHighlights, currentPassageHtml, currentReadingAnnotations]);
  
  // Set apiResult from prop when in review mode
  useEffect(() => {
    if (apiResultProp) {
      setApiResult(apiResultProp);
    }
  }, [apiResultProp]);
  
  // Stop timer in review mode
  useEffect(() => {
    if (isReviewMode) {
      setIsTimerRunning(false);
    }
  }, [isReviewMode]);
  
  useEffect(() => {
    let interval;
    
    if (isTimerRunning && timer.minutes !== 'Unlimited') {
      interval = setInterval(() => {
        setTimer(prevTimer => {
          if (prevTimer.minutes === 0 && prevTimer.seconds === 0) {
            clearInterval(interval);
            handleApiSubmit();
            return prevTimer;
          }
          
          if (prevTimer.seconds === 0) {
            return { minutes: prevTimer.minutes - 1, seconds: 59 };
          } else {
            return { ...prevTimer, seconds: prevTimer.seconds - 1 };
          }
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, timer.minutes]);

  useEffect(() => {
    if (sections && sections[currentPart-1] && sections[currentPart-1].passage_text) {
      const { cleanedHtml, blankToNumber, selectionToNumber } = extractBlankNumberMappingAndCleanHtml(sections[currentPart-1].passage_text);
      setCleanedPassageText(markTFNInfoTables(processPassageText(cleanedHtml)));
      // Store id->questionNumber mappings so hydration doesn't rely on index order
      setBlankIdToNumberMap(blankToNumber || {});
      setSelectionIdToNumberMap(selectionToNumber || {});
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections[currentPart-1].passage_text, 'text/html');
      const numbers = [];
      const tableNumbers = [];
      doc.querySelectorAll('table .tiptap-input-field').forEach(span => {
        let node = span;
        let qNum = '';
        while (node && node !== doc.body) {
          let answerWrapper = node.querySelector && node.querySelector('.answer-wrapper');
          if (!answerWrapper && node.classList && node.classList.contains('answer-wrapper')) {
            answerWrapper = node;
          }
          if (answerWrapper && answerWrapper.getAttribute('data-question-number')) {
            qNum = answerWrapper.getAttribute('data-question-number');
            break;
          }
          node = node.parentNode;
        }
        tableNumbers.push(qNum);
      });
      doc.querySelectorAll('p').forEach(p => {
        if (p.closest('.question-box')) return; // Skip question-box paragraphs, handled separately
        
        const pHTML = p.innerHTML;
        // Find all markers and blanks in order
        const markers = [];
        const blanks = [];
        
        // Find all angle bracket markers with their positions
        const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
        let markerMatch;
        while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
          markers.push({
            number: markerMatch[2] || markerMatch[3],
            index: markerMatch.index
          });
        }
        
        // Find all blank spans
        p.querySelectorAll('.tiptap-input-field').forEach(span => {
          if (!span.closest('table') && !span.closest('.question-box')) {
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              blanks.push({
                span: span,
                index: spanIndex
              });
            }
          }
        });
        
        // Match each blank to its nearest preceding marker
        blanks.forEach(blank => {
          // Find the marker that comes just before this blank
          let matchedMarker = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < blank.index) {
              matchedMarker = markers[i];
              break;
            }
          }
          
          if (matchedMarker) {
            numbers.push(matchedMarker.number);
          } else {
            numbers.push('');
          }
        });
      });
      doc.querySelectorAll('.question-box').forEach(box => {
        const boxContent = box.querySelector('.question-box-content');
        if (boxContent) {
          boxContent.querySelectorAll('p').forEach(p => {
            const pHTML = p.innerHTML;
            // Find all markers and blanks in order
            const markers = [];
            const blanks = [];
            
            // Find all angle bracket markers with their positions
            const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
            let markerMatch;
            while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
              markers.push({
                number: markerMatch[2] || markerMatch[3],
                index: markerMatch.index
              });
            }
            
            // Find all blank spans
            p.querySelectorAll('.tiptap-input-field').forEach(span => {
              if (!span.closest('table')) {
                const spanHTML = span.outerHTML;
                const spanIndex = pHTML.indexOf(spanHTML);
                if (spanIndex !== -1) {
                  blanks.push({
                    span: span,
                    index: spanIndex
                  });
                }
              }
            });
            
            // Match each blank to its nearest preceding marker
            blanks.forEach(blank => {
              // Find the marker that comes just before this blank
              let matchedMarker = null;
              for (let i = markers.length - 1; i >= 0; i--) {
                if (markers[i].index < blank.index) {
                  matchedMarker = markers[i];
                  break;
                }
              }
              
              if (matchedMarker) {
                numbers.push(matchedMarker.number);
              } else {
                numbers.push('');
              }
            });
          });
        }
      });
      setBlankNumbersInOrder(numbers);
      setTableBlankNumbersInOrder(tableNumbers);
      const selectionNumbers = [];
      // Non-question-box paragraphs: map each selection to nearest preceding marker
      doc.querySelectorAll('p').forEach(p => {
        if (p.closest('.question-box')) return;
        const pHTML = p.innerHTML;
        const markers = [];
        const selects = [];
        const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
        let m;
        while ((m = markerRegex.exec(pHTML)) !== null) {
          markers.push({ number: m[2] || m[3], index: m.index });
        }
        p.querySelectorAll('.selection-field').forEach(span => {
          if (!span.closest('table') && !span.closest('.question-box')) {
            const html = span.outerHTML;
            const idx = pHTML.indexOf(html);
            if (idx !== -1) selects.push({ index: idx });
          }
        });
        selects.forEach(sel => {
          let matched = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < sel.index) { matched = markers[i]; break; }
          }
          selectionNumbers.push(matched ? matched.number : '');
        });
      });
      // Inside question-box paragraphs
      doc.querySelectorAll('.question-box').forEach(box => {
        const boxContent = box.querySelector('.question-box-content');
        if (!boxContent) return;
        boxContent.querySelectorAll('p').forEach(p => {
          const pHTML = p.innerHTML;
          const markers = [];
          const selects = [];
          const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
          let m;
          while ((m = markerRegex.exec(pHTML)) !== null) {
            markers.push({ number: m[2] || m[3], index: m.index });
          }
          p.querySelectorAll('.selection-field').forEach(span => {
            if (!span.closest('table')) {
              const html = span.outerHTML;
              const idx = pHTML.indexOf(html);
              if (idx !== -1) selects.push({ index: idx });
            }
          });
          selects.forEach(sel => {
            let matched = null;
            for (let i = markers.length - 1; i >= 0; i--) {
              if (markers[i].index < sel.index) { matched = markers[i]; break; }
            }
            selectionNumbers.push(matched ? matched.number : '');
          });
        });
      });
      const tableSelectionNumbers = [];
      doc.querySelectorAll('table .selection-field').forEach(span => {
        let node = span;
        let qNum = '';
        while (node && node !== doc.body) {
          let answerWrapper = node.querySelector && node.querySelector('.answer-wrapper');
          if (!answerWrapper && node.classList && node.classList.contains('answer-wrapper')) {
            answerWrapper = node;
          }
          if (answerWrapper && answerWrapper.getAttribute('data-question-number')) {
            qNum = answerWrapper.getAttribute('data-question-number');
            break;
          }
          node = node.parentNode;
        }
        tableSelectionNumbers.push(qNum);
      });
      setSelectionNumbersInOrder(selectionNumbers);
      setTableSelectionNumbersInOrder(tableSelectionNumbers);
      setSelectionNumbersFinal(selectionNumbers);
    } else {
      setSelectionNumbersInOrder([]);
      setTableSelectionNumbersInOrder([]);
      setSelectionNumbersFinal([]);
      setBlankNumbersInOrder([]);
      setTableBlankNumbersInOrder([]);
      setCleanedPassageText('');
    }
  }, [sections, currentPart]);

  const handlePartChange = (partNumber) => {
    setCurrentPart(partNumber);
    setCurrentQuestion(1);
  };
  
  const handleQuestionSelect = (questionNumber) => {
    if ((isReviewMode || apiResult) && scrollToOfficialAnswerHighlight(questionNumber)) {
      return;
    }

    const element = document.getElementById(`question-${questionNumber}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const storeCurrentReadingAnnotations = useCallback((annotations) => {
    setReadingAnnotations((previous) => ({
      version: READING_ANNOTATION_VERSION,
      passages: {
        ...previous.passages,
        [currentPassageKey]: annotations,
      },
    }));
  }, [currentPassageKey]);

  const captureReadingSelection = useCallback(() => {
    if (isReviewMode || apiResult) return;
    window.setTimeout(() => {
      const container = passageRef.current;
      const selection = window.getSelection();
      if (!container || !selection || selection.isCollapsed || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const offsets = getReadingSelectionOffsets(container, range);
      if (!offsets || !offsets.text.trim()) return;

      const rect = range.getBoundingClientRect();
      setReadingSelection({
        ...offsets,
        passageKey: currentPassageKey,
        rect: {
          left: rect.left,
          right: rect.right,
          top: rect.top,
          bottom: rect.bottom,
        },
      });
    }, 0);
  }, [apiResult, currentPassageKey, isReviewMode]);

  const applyReadingSelection = (type) => {
    if (!readingSelection || readingSelection.passageKey !== currentPassageKey) return;
    const newId = typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const next = mergeReadingAnnotation(currentPassageHtml, currentReadingAnnotations, {
      id: newId,
      type,
      start: readingSelection.start,
      end: readingSelection.end,
      text: readingSelection.text,
      note: '',
    });
    const saved = next.find((annotation) => (
      annotation.start <= readingSelection.start && annotation.end >= readingSelection.end
    ));

    storeCurrentReadingAnnotations(next);
    setReadingSelection(null);
    window.getSelection()?.removeAllRanges();

    if (type === 'note' && saved) {
      setActiveReadingAnnotation({ ...saved, passageKey: currentPassageKey });
      setReadingNoteDraft(saved.note || '');
    }
  };

  const handlePassageClick = (event) => {
    const officialInfo = event.target.closest?.('[data-official-info="true"]');
    if (officialInfo) {
      const officialMark = officialInfo.closest('[data-official-highlight-id]');
      setOfficialExplanation(sanitizeOfficialExplanationHtml(officialMark?.dataset.explanation || 'No explanation available.'));
      return;
    }

    const userMark = event.target.closest?.('[data-reading-highlight-id]');
    if (!userMark) return;
    const annotation = currentReadingAnnotations.find((item) => item.id === userMark.dataset.readingHighlightId);
    if (!annotation) return;
    setActiveReadingAnnotation({ ...annotation, passageKey: currentPassageKey });
    setReadingNoteDraft(annotation.note || '');
  };

  const handlePassageKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!event.target.closest?.('[data-reading-highlight-id], [data-official-info="true"]')) return;
    event.preventDefault();
    handlePassageClick(event);
  };

  const removeActiveReadingAnnotation = () => {
    if (!activeReadingAnnotation || activeReadingAnnotation.passageKey !== currentPassageKey) return;
    storeCurrentReadingAnnotations(
      currentReadingAnnotations.filter((annotation) => annotation.id !== activeReadingAnnotation.id)
    );
    setActiveReadingAnnotation(null);
    setReadingNoteDraft('');
  };

  const saveReadingNote = () => {
    if (!activeReadingAnnotation || activeReadingAnnotation.passageKey !== currentPassageKey) return;
    storeCurrentReadingAnnotations(currentReadingAnnotations.map((annotation) => (
      annotation.id === activeReadingAnnotation.id
        ? { ...annotation, type: 'note', note: readingNoteDraft.trim().slice(0, 1000) }
        : annotation
    )));
    setActiveReadingAnnotation(null);
    setReadingNoteDraft('');
  };

  useEffect(() => {
    setReadingSelection(null);
    setActiveReadingAnnotation(null);
    setReadingNoteDraft('');
    setOfficialExplanation('');
  }, [currentPassageKey]);

  const handleMouseDown = (e) => {
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const bounds = containerRef.current.getBoundingClientRect();
      let percent = ((e.clientX - bounds.left) / bounds.width) * 100;
      if (percent < LEFT_WIDTH_MIN) percent = LEFT_WIDTH_MIN;
      if (percent > LEFT_WIDTH_MAX) percent = LEFT_WIDTH_MAX;
      setLeftWidth(percent);
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  useEffect(() => {
    if (dragging) {
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.userSelect = '';
    }
    return () => {
      document.body.style.userSelect = '';
    };
  }, [dragging]);

  const handleInputChange = (id, value) => {
    setInputValues((prev) => ({ ...prev, [id]: value }));
  };

  const answersRef = useRef({});

  const handleHydratedAnswerChange = (questionKey, value) => {
    setAllAnswers(prev => ({ ...prev, [questionKey]: value }));
    answersRef.current[questionKey] = value;
  };

  const draftIdentity = useMemo(() => ({
    scope: fullTestAttemptId ? 'full' : 'sectional',
    section: 'reading',
    testId,
    groupId,
    fullTestAttemptId,
  }), [fullTestAttemptId, groupId, testId]);
  const readingDraftState = useMemo(() => ({
    answers: allAnswers,
    currentStep: {
      currentPart,
      currentQuestion,
      mode,
      selectedSectionIndices,
      readingAnnotations,
    },
    timeState: { timer },
    audioState: {},
  }), [allAnswers, currentPart, currentQuestion, mode, readingAnnotations, selectedSectionIndices, timer]);
  const restoreReadingDraft = useCallback((draft) => {
    const restoredAnswers = draft?.answers && typeof draft.answers === 'object' && !Array.isArray(draft.answers)
      ? draft.answers
      : null;
    if (restoredAnswers) {
      setAllAnswers(restoredAnswers);
      setInputValues((prev) => ({ ...prev, ...restoredAnswers }));
      setSelectionValues((prev) => ({ ...prev, ...restoredAnswers }));
      answersRef.current = { ...restoredAnswers };
    }
    const restoredPart = parseInt(draft?.currentStep?.currentPart, 10);
    if (Number.isFinite(restoredPart)) {
      setCurrentPart(Math.max(1, Math.min(restoredPart, Math.max(sections.length, 1))));
    }
    const restoredQuestion = parseInt(draft?.currentStep?.currentQuestion, 10);
    if (Number.isFinite(restoredQuestion)) {
      setCurrentQuestion(Math.max(1, restoredQuestion));
    }
    if (draft?.timeState?.timer && !isReviewMode) {
      setTimer(draft.timeState.timer);
    }
    const storedAnnotations = draft?.currentStep?.readingAnnotations;
    if (storedAnnotations?.version === READING_ANNOTATION_VERSION
      && storedAnnotations.passages
      && typeof storedAnnotations.passages === 'object') {
      const restoredPassages = {};
      sections.forEach((section, partIndex) => {
        const passageKey = getReadingPassageKey(section, partIndex, selectedSectionIndices);
        const restored = restoreReadingAnnotations(
          section?.passage_html || '',
          storedAnnotations.passages[passageKey]
        );
        if (restored.length) restoredPassages[passageKey] = restored;
      });
      setReadingAnnotations({
        version: READING_ANNOTATION_VERSION,
        passages: restoredPassages,
      });
    }
  }, [isReviewMode, sections, selectedSectionIndices]);
  const { clearDraft } = useIeltsAutosave({
    enabled: !isReviewMode && Boolean(testId),
    restoreEnabled: restoreDraft,
    identity: draftIdentity,
    draftState: readingDraftState,
    onRestore: restoreReadingDraft,
  });

  useEffect(() => {
    const resetKey = [
      testId || '',
      groupId || '',
      fullTestAttemptId || '',
      restoreDraft ? 'resume' : 'fresh',
      isReviewMode ? 'review' : 'active',
    ].join(':');

    if (highlightResetRef.current === resetKey) return;
    highlightResetRef.current = resetKey;

    if (!isReviewMode && !restoreDraft) {
      setReadingAnnotations({
        version: READING_ANNOTATION_VERSION,
        passages: {},
      });
    }
  }, [fullTestAttemptId, groupId, isReviewMode, restoreDraft, testId]);

  const explanationMap = React.useMemo(() => {
    if (!explanation) return {};
    try {
      const cleaned = explanation.trim().replace(/^{|}$/g, '');
      const pairs = cleaned.split(/,(?=\d+:)/);
      const obj = {};
      pairs.forEach(pair => {
        const idx = pair.indexOf(':');
        if (idx > 0) {
          const key = pair.slice(0, idx).trim();
          const value = pair.slice(idx + 1).trim();
          obj[key] = value;
        }
      });
      return obj;
    } catch {
      return {};
    }
  }, [explanation]);

  useEffect(() => {
    const el = document.querySelector('.ielts-passage-questions');
    if (el) {
      let globalTooltipPopup = document.getElementById('custom-tooltip-popup');
      if (!globalTooltipPopup) {
        globalTooltipPopup = document.createElement('div');
        globalTooltipPopup.id = 'custom-tooltip-popup';
        globalTooltipPopup.style.position = 'fixed';
        globalTooltipPopup.style.background = '#fff';
        globalTooltipPopup.style.color = '#333';
        globalTooltipPopup.style.border = '1px solid #996CFE';
        globalTooltipPopup.style.borderRadius = '6px';
        globalTooltipPopup.style.padding = '8px 14px';
        globalTooltipPopup.style.fontSize = '15px';
        globalTooltipPopup.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
        globalTooltipPopup.style.zIndex = 9999;
        globalTooltipPopup.style.display = 'none';
        globalTooltipPopup.style.pointerEvents = 'none';
        document.body.appendChild(globalTooltipPopup);
      }
      function showTooltipAt(elem, text) {
        const rect = elem.getBoundingClientRect();
        globalTooltipPopup.textContent = text;
        globalTooltipPopup.style.display = 'block';
        globalTooltipPopup.style.pointerEvents = 'auto';

        let left = rect.right + 8;
        let top = rect.top - 4;

        const popupWidth = globalTooltipPopup.offsetWidth || 200;
        if (left + popupWidth > window.innerWidth) {
          left = rect.left - popupWidth - 8;
          if (left < 0) left = 8;
        }

        globalTooltipPopup.style.left = `${left}px`;
        globalTooltipPopup.style.top = `${top}px`;

        clearTimeout(globalTooltipPopup._hideTimeout);
        globalTooltipPopup._hideTimeout = setTimeout(() => {
          globalTooltipPopup.style.display = 'none';
        }, 2000);
      }
      document.addEventListener('click', (e) => {
        if (!e.target.classList.contains('question-tooltip')) {
          globalTooltipPopup.style.display = 'none';
        }
      });
      const blanks = el.querySelectorAll('span[data-blank-id]');
      // Defensive: detect legacy duplicate ids and fall back to order mapping when duplicates exist
      const blankIdCounts = {};
      blanks.forEach((span) => {
        const id = span.getAttribute('data-blank-id');
        if (id) {
          blankIdCounts[id] = (blankIdCounts[id] || 0) + 1;
        }
      });
      let tableIdx = 0;
      let normalIdx = 0;
      blanks.forEach((span) => {
        const isInTable = !!span.closest('table');
        const blankId = span.getAttribute('data-blank-id');
        const mapped = blankId && blankIdToNumberMap && blankIdToNumberMap[blankId];
        let qNum;
        if (isInTable) {
          // Prefer marker-based number for question-box table blanks (e.g. 1, 2 instead of "1-2")
          qNum = mapped || tableBlankNumbersInOrder[tableIdx] || '';
          if (!mapped) tableIdx++;
        } else {
          const isDuplicateId = blankId && blankIdCounts[blankId] > 1;
          qNum = (!isDuplicateId && mapped) ? mapped : (blankNumbersInOrder[normalIdx] || '');
          normalIdx++;
        }
        
        // Fallback: Check parent question-box for question number (single number only)
        if (!qNum || !/^\d+$/.test(qNum)) {
          const questionBox = span.closest('.question-box');
          if (questionBox) {
            const boxQNum = questionBox.getAttribute('data-question-number') || questionBox.getAttribute('questionnumber');
            if (boxQNum && /^\d+$/.test(boxQNum)) {
              qNum = boxQNum;
            }
          }
        }

        const correctAnswer = apiResult?.response?.analytics?.[qNum]?.correctAnswer;
        const userAnswer = apiResult?.response?.analytics?.[qNum]?.userAnswer;
        const isCorrect = apiResult?.response?.analytics?.[qNum]?.isCorrect;

        if (!span.querySelector('input')) {
          const input = document.createElement('input');
          input.type = 'text';
          input.className = 'fill-blank-input';
          const displayValue = (apiResult && userAnswer !== undefined && userAnswer !== null)
            ? userAnswer
            : (allAnswers[qNum] !== undefined ? allAnswers[qNum] : '');
          input.value = displayValue;
          input.placeholder = qNum;
          input.style.width = '90px';
          input.style.margin = '0 4px';
          input.style.border = '1px solid #996CFE';
          input.style.borderRadius = '4px';
          input.style.padding = '2px 6px';
          input.style.fontSize = '15px';
          input.style.color = '#333';
          input.disabled = !!apiResult;

          if (apiResult) {
            if (isCorrect) {
              input.style.backgroundColor = '#E8F5E9';
              input.style.border = '1px solid #37965E';
            } else {
              input.style.backgroundColor = '#FFF3F3';
              input.style.border = '1px solid #E53935';
            }
          }

          input.oninput = (e) => {
            handleInputChange(qNum, e.target.value);
            handleHydratedAnswerChange(qNum, e.target.value);
          };

          const container = document.createElement('div');
          container.style.display = 'inline-flex';
          container.style.alignItems = 'center';
          container.style.position = 'relative';

          container.appendChild(input);

          if (apiResult && !isCorrect && correctAnswer !== undefined && correctAnswer !== null) {
            const correctAnswerDiv = document.createElement('div');
            correctAnswerDiv.style.marginLeft = '12px';
            correctAnswerDiv.style.marginRight = '16px';
            correctAnswerDiv.style.fontSize = '13px';
            correctAnswerDiv.style.color = '#37965E';
            correctAnswerDiv.style.fontWeight = '600';
            correctAnswerDiv.style.padding = '2px 8px';
            correctAnswerDiv.style.backgroundColor = '#E8F5E9';
            correctAnswerDiv.style.borderRadius = '4px';
            correctAnswerDiv.style.border = '1px solid #37965E';
            correctAnswerDiv.style.lineHeight = '1.4';
            correctAnswerDiv.style.display = 'inline-flex';
            correctAnswerDiv.style.alignItems = 'center';
            correctAnswerDiv.innerHTML = `<span style="color: #666; font-weight: 500;">Correct: </span>${correctAnswer}`;
            container.appendChild(correctAnswerDiv);
          }

          if (apiResult) {
            const tooltipIconBlank = document.createElement('span');
            tooltipIconBlank.className = 'question-tooltip';
            tooltipIconBlank.style.marginLeft = '8px';
            tooltipIconBlank.style.marginRight = '8px';
            tooltipIconBlank.style.cursor = 'pointer';
            tooltipIconBlank.title = 'Random tooltip text';
            tooltipIconBlank.innerHTML = `<svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="10" fill="#996CFE"/><text x="10" y="15" text-anchor="middle" fill="#fff" font-size="14" font-family="Arial" dy="-2">i</text></svg>`;
            container.appendChild(tooltipIconBlank);

            const tooltipTextBlank = explanationMap[qNum] || 'No explanation available.';
            tooltipIconBlank.onclick = (e) => {
              e.stopPropagation();
              showTooltipAt(tooltipIconBlank, tooltipTextBlank);
            };
          }

          span.innerHTML = '';
          span.appendChild(container);
        } else {
          const input = span.querySelector('input');
          if (input) {
            const displayValue = (apiResult && userAnswer !== undefined && userAnswer !== null)
              ? userAnswer
              : (allAnswers[qNum] !== undefined ? allAnswers[qNum] : '');
            input.value = displayValue;
            input.disabled = !!apiResult;

            if (apiResult) {
              if (isCorrect) {
                input.style.backgroundColor = '#E8F5E9';
                input.style.border = '1px solid #37965E';
              } else {
                input.style.backgroundColor = '#FFF3F3';
                input.style.border = '1px solid #E53935';
              }
            }

            if (apiResult && !isCorrect && correctAnswer !== undefined && correctAnswer !== null) {
              let correctAnswerDiv = span.querySelector('.correct-answer');
              if (!correctAnswerDiv) {
                correctAnswerDiv = document.createElement('div');
                correctAnswerDiv.className = 'correct-answer';
                correctAnswerDiv.style.marginLeft = '12px';
                correctAnswerDiv.style.marginRight = '16px';
                correctAnswerDiv.style.fontSize = '13px';
                correctAnswerDiv.style.color = '#37965E';
                correctAnswerDiv.style.fontWeight = '600';
                correctAnswerDiv.style.padding = '2px 8px';
                correctAnswerDiv.style.backgroundColor = '#E8F5E9';
                correctAnswerDiv.style.borderRadius = '4px';
                correctAnswerDiv.style.border = '1px solid #37965E';
                correctAnswerDiv.style.lineHeight = '1.4';
                correctAnswerDiv.style.display = 'inline-flex';
                correctAnswerDiv.style.alignItems = 'center';
                span.appendChild(correctAnswerDiv);
              }
              correctAnswerDiv.innerHTML = `<span style="color: #666; font-weight: 500;">Correct: </span>${correctAnswer}`;
            }
          }
        }
      });
      const selectionFields = el.querySelectorAll('span.selection-field');
      // Defensive: detect legacy duplicate selection ids as well
      const selectionIdCounts = {};
      selectionFields.forEach((span) => {
        const id = span.getAttribute('id');
        if (id) {
          selectionIdCounts[id] = (selectionIdCounts[id] || 0) + 1;
        }
      });
      let selectionIdx = 0;
      let tableSelectionIdx = 0;
      selectionFields.forEach((span, idx) => {
        const optionsAttr = span.getAttribute('data-options') || span.getAttribute('options') || '';
        const options = optionsAttr.split(',').map(opt => opt.trim());
        const isInTable = !!span.closest('table');
        const selectionId = span.getAttribute('id');
        const mapped = selectionId && selectionIdToNumberMap && selectionIdToNumberMap[selectionId];
        let qNum = '';
        if (isInTable) {
          // Prefer marker-based number for question-box table dropdowns (e.g. 1, 2 instead of "1-2")
          qNum = mapped || tableSelectionNumbersInOrder[tableSelectionIdx] || '';
          if (!mapped) tableSelectionIdx++;
        } else {
          const isDuplicateId = selectionId && selectionIdCounts[selectionId] > 1;
          qNum = (!isDuplicateId && mapped)
            ? mapped
            : (selectionNumbersFinal[selectionIdx] || '');
          selectionIdx++;
          
          if (!qNum || !/^\d+$/.test(qNum)) {
            const section = sections[currentPart - 1];
            
            if (section && section.questions) {
              const questionBox = section.questions.find(q => q.question_type === 'question-box');
              
              if (questionBox && questionBox.sample_answer) {
                try {
                  const sampleAnswer = typeof questionBox.sample_answer === 'string' 
                    ? JSON.parse(questionBox.sample_answer) 
                    : questionBox.sample_answer;
                  
                  if (typeof sampleAnswer === 'object' && sampleAnswer !== null) {
                    const qNumsFromSample = Object.keys(sampleAnswer).filter(k => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
                    
                    if (qNumsFromSample.length > 0) {
                      if (qNumsFromSample.length === 1) {
                        qNum = qNumsFromSample[0];
                      } else {
                        const currentDropdownIndex = selectionIdx - 1;
                        if (currentDropdownIndex < qNumsFromSample.length) {
                          qNum = qNumsFromSample[currentDropdownIndex];
                        } else {
                          qNum = qNumsFromSample[0];
                        }
                      }
                      
                    }
                  }
                } catch (e) {
                  
                }
              }
            }
            
            if (!qNum || !/^\d+$/.test(qNum)) {
              const sectionQuestions = getSectionQuestionNumbers(currentPart - 1);
              
              const currentDropdownIndex = selectionIdx - 1;
              if (sectionQuestions.length > currentDropdownIndex) {
                qNum = sectionQuestions[currentDropdownIndex];
              } else if (sectionQuestions.length > 0) {
                qNum = sectionQuestions[0];
              }
            }
          }
        }
        
        if (!qNum || !/^\d+$/.test(qNum)) {
          return;
        }

        const correctAnswer = apiResult?.response?.analytics?.[qNum]?.correctAnswer;
        const userAnswer = apiResult?.response?.analytics?.[qNum]?.userAnswer;
        const isCorrect = apiResult?.response?.analytics?.[qNum]?.isCorrect;

        // Question numbers removed - no longer displaying question numbers for selection fields

        const container = document.createElement('div');
        container.style.display = 'inline-flex';
        container.style.alignItems = 'center';
        container.style.position = 'relative';

        const select = document.createElement('select');
        select.className = 'dropdown-selection-field';
        select.style.border = '1px solid #996CFE';
        select.style.borderRadius = '4px';
        select.style.padding = '2px 6px';
        select.style.fontSize = '15px';
        select.style.color = '#333';
        select.style.marginRight = '4px';
        select.disabled = !!apiResult;

        if (apiResult) {
          if (isCorrect) {
            select.style.backgroundColor = '#E8F5E9';
            select.style.border = '1px solid #37965E';
          } else {
            select.style.backgroundColor = '#FFF3F3';
            select.style.border = '1px solid #E53935';
          }
        }

        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = 'Select';
        defaultOption.disabled = true;
        defaultOption.selected = true;
        select.appendChild(defaultOption);

        options.forEach(opt => {
          const optElem = document.createElement('option');
          optElem.value = opt;
          optElem.textContent = opt;
          select.appendChild(optElem);
        });

        if (!qNum || !/^\d+$/.test(qNum)) {
          return;
        }
        
        const displayValue = (apiResult && userAnswer !== undefined && userAnswer !== null)
          ? userAnswer
          : (allAnswers[qNum] !== undefined ? allAnswers[qNum] : '');
        select.value = displayValue;
        select.onchange = (e) => {
          handleHydratedAnswerChange(qNum, e.target.value);
        };

        container.appendChild(select);

        if (apiResult && !isCorrect && correctAnswer !== undefined && correctAnswer !== null) {
          const correctAnswerDiv = document.createElement('div');
          correctAnswerDiv.style.marginLeft = '12px';
          correctAnswerDiv.style.marginRight = '16px';
          correctAnswerDiv.style.fontSize = '13px';
          correctAnswerDiv.style.color = '#37965E';
          correctAnswerDiv.style.fontWeight = '600';
          correctAnswerDiv.style.padding = '2px 8px';
          correctAnswerDiv.style.backgroundColor = '#E8F5E9';
          correctAnswerDiv.style.borderRadius = '4px';
          correctAnswerDiv.style.border = '1px solid #37965E';
          correctAnswerDiv.style.lineHeight = '1.4';
          correctAnswerDiv.style.display = 'inline-flex';
          correctAnswerDiv.style.alignItems = 'center';
          correctAnswerDiv.innerHTML = `<span style="color: #666; font-weight: 500;">Correct: </span>${correctAnswer}`;
          container.appendChild(correctAnswerDiv);
        }

        if (apiResult) {
          const tooltipIconDropdown = document.createElement('span');
          tooltipIconDropdown.className = 'question-tooltip';
          tooltipIconDropdown.style.marginLeft = '8px';
          tooltipIconDropdown.style.marginRight = '8px';
          tooltipIconDropdown.style.cursor = 'pointer';
          tooltipIconDropdown.title = 'Random tooltip text';
          tooltipIconDropdown.innerHTML = `<svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="10" fill="#996CFE"/><text x="10" y="15" text-anchor="middle" fill="#fff" font-size="14" font-family="Arial" dy="-2">i</text></svg>`;
          container.appendChild(tooltipIconDropdown);

          const tooltipTextDropdown = explanationMap[qNum] || 'No explanation available.';
          tooltipIconDropdown.onclick = (e) => {
            e.stopPropagation();
            showTooltipAt(tooltipIconDropdown, tooltipTextDropdown);
          };
        }

        span.innerHTML = '';
        span.appendChild(container);
      });
      
      // Hydrate MCQ containers using shared function
      hydrateMCQContainers(el, {
        allAnswers,
        apiResult,
        onAnswerChange: handleHydratedAnswerChange
      });
    }
  // eslint-disable-next-line
  }, [cleanedPassageText, selectionValues, currentPart, allAnswers, forceUpdate, apiResult, sections, explanationMap]);

  useEffect(() => {
    hydrateDragDropFields({
      roots: [
        document.querySelector('.ielts-passage-html'),
        document.querySelector('.ielts-passage-questions'),
      ],
      sections,
      sectionIdx: currentPart - 1,
      allAnswers,
      apiResult,
      onAnswerChange: handleHydratedAnswerChange,
    });
  }, [sections, currentPart, cleanedPassageText, allAnswers, apiResult]);

  const getVisibleQuestionCount = () => {
    const el = document.querySelector('.ielts-passage-questions');
    const seen = new Set();
    let count = 0;
    if (el) {
      const selects = el.querySelectorAll('select.dropdown-selection-field');
      selects.forEach((select, idx) => {
        let qNum = null;
        let parent = select.closest('.answer-wrapper');
        if (parent) {
          qNum = parent.getAttribute('data-question-number');
        }
        if (!qNum) {
          parent = select.closest('span.selection-field') || select.parentElement;
          qNum = parent?.getAttribute('data-question-number');
        }
        if (!qNum) qNum = (idx + 1).toString();
        qNum = qNum.trim();
        if (!seen.has(qNum) && select.offsetParent !== null) {
          seen.add(qNum);
          count++;
        }
      });
      const blanks = el.querySelectorAll('input.fill-blank-input');
      blanks.forEach((input, idx) => {
        let qNum = null;
        let parent = input.closest('.answer-wrapper');
        if (parent) {
          qNum = parent.getAttribute('data-question-number');
        }
        if (!qNum) {
          parent = input.parentElement;
          qNum = parent?.getAttribute('data-question-number');
        }
        if (!qNum) qNum = (selects.length + idx + 1).toString();
        qNum = qNum.trim();
        if (!seen.has(qNum) && input.offsetParent !== null) {
          seen.add(qNum);
          count++;
        }
      });
    }
    return count;
  };

  function parseAnswerSheet(answerSheetStr) {
    if (!answerSheetStr) return {};
    
    const cleaned = answerSheetStr.trim().replace(/^{|}$/g, '');
    const pairs = cleaned.split(/,(?=\d+:)/);
    const obj = {};
    pairs.forEach(pair => {
      const idx = pair.indexOf(':');
      if (idx > 0) {
        const key = pair.slice(0, idx).trim();
        const value = pair.slice(idx + 1).trim();
        obj[key] = value;
      }
    });
    return obj;
  }

  // Create mapping from question numbers to database question IDs
  const questionNumberToDbIdMap = useMemo(() => {
    return createQuestionNumberToDbIdMap(sections);
  }, [sections]);

  // Check for skipped questions - getSectionQuestionNumbers is defined in the component
  const checkSkippedQuestionsCallback = useCallback(() => {
    const skipped = checkSkippedQuestions(sections, allAnswers, getSectionQuestionNumbers);
    setSkippedQuestions(skipped);
    return skipped;
  }, [sections, allAnswers]); // getSectionQuestionNumbers is stable, doesn't need to be in deps

  const handleApiSubmit = async () => {
    setIsTimerRunning(false);
    setIsSubmitting(true);
    
    const endTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
    setTestEndTime(endTime);
    
    const answers = { ...allAnswers };
    Object.assign(answers, answersRef.current);
    
    
    const allQuestionNumbers = [];
    sections.forEach((section, idx) => {
      const sectionQuestions = getSectionQuestionNumbers(idx);
      allQuestionNumbers.push(...sectionQuestions);
    });
    
    
    
    const answersArray = buildAnswersArray(answers, questionNumberToDbIdMap);
    
    const payload = {
      testId: Number(testId),
      userId: Number(userId),
      answers: answersArray,
      start_time: testStartTime,
      end_time: endTime,
      ...(groupId ? { groupId } : {}),
      ...(groupId && fullTestAttemptId ? { fullTestAttemptId } : {}),
      ...(groupId ? { is_speaking_optional: localStorage.getItem('is_speaking_optional') === 'true' } : {}),
      ...getObjectiveScoringMetadata({ mode, selectedSectionIndices, totalPartCount, groupId }),
    };
    try {
      const response = await fetch(`${Base_URL}/app/users/ielts-tests/saveTestAttempt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredLaunchGrant({ exam: 'ielts', scope: groupId ? 'full' : 'sectional', id: groupId || testId })
            ? { 'X-Test-Launch-Token': getStoredLaunchGrant({ exam: 'ielts', scope: groupId ? 'full' : 'sectional', id: groupId || testId }) }
            : {}),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data && data.response) {
        setApiResult(data);
        setForceUpdate(prev => prev + 1);
        clearDraft().catch(() => {});
        if (onComplete) onComplete(data);
      }
    } catch (error) {
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndExit = () => {
    navigate('/ielts-dashboard');
  };

  const handleSubmitWithWarning = () => {
    const skipped = checkSkippedQuestionsCallback();
    if (skipped.length > 0) {
      setShowSkipWarning(true);
    } else {
      handleApiSubmit();
    }
  };

  function getSectionQuestionNumbers(sectionIdx) {
    let blankNums = [];
    let tableBlankNums = [];
    let selectionNums = [];
    let tableSelectionNums = [];
    let mcqNums = [];
    let dragDropNums = [];
    if (sections[sectionIdx]) {
      const passageText = sections[sectionIdx].passage_text || '';
      const parser = new DOMParser();
      const doc = parser.parseFromString(`${sections[sectionIdx].passage_html || ''}${passageText}`, 'text/html');
      
      // Process regular paragraphs (not in question-box)
      doc.querySelectorAll('p').forEach(p => {
        if (p.closest('.question-box')) return; // Skip question-box paragraphs, handled separately
        
        const pHTML = p.innerHTML;
        const markers = [];
        const blanks = [];
        
        // Find all angle bracket markers with their positions
        const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
        let markerMatch;
        while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
          markers.push({
            number: markerMatch[2] || markerMatch[3],
            index: markerMatch.index
          });
        }
        
        // Find all blank spans
        p.querySelectorAll('.tiptap-input-field').forEach(span => {
          if (!span.closest('table') && !span.closest('.question-box')) {
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              blanks.push({
                span: span,
                index: spanIndex
              });
            }
          }
        });
        
        // Match each blank to its nearest preceding marker
        blanks.forEach(blank => {
          let matchedMarker = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < blank.index) {
              matchedMarker = markers[i];
              break;
            }
          }
          if (matchedMarker) {
            blankNums.push(matchedMarker.number);
          } else {
            blankNums.push('');
          }
        });
      });
      
      // Process question-box paragraphs
      doc.querySelectorAll('.question-box').forEach(box => {
        const boxContent = box.querySelector('.question-box-content');
        if (boxContent) {
          boxContent.querySelectorAll('p').forEach(p => {
            const pHTML = p.innerHTML;
            const markers = [];
            const blanks = [];
            
            // Find all angle bracket markers with their positions
            const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
            let markerMatch;
            while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
              markers.push({
                number: markerMatch[2] || markerMatch[3],
                index: markerMatch.index
              });
            }
            
            // Find all blank spans
            p.querySelectorAll('.tiptap-input-field').forEach(span => {
              if (!span.closest('table')) {
                const spanHTML = span.outerHTML;
                const spanIndex = pHTML.indexOf(spanHTML);
                if (spanIndex !== -1) {
                  blanks.push({
                    span: span,
                    index: spanIndex
                  });
                }
              }
            });
            
            // Match each blank to its nearest preceding marker
            blanks.forEach(blank => {
              let matchedMarker = null;
              for (let i = markers.length - 1; i >= 0; i--) {
                if (markers[i].index < blank.index) {
                  matchedMarker = markers[i];
                  break;
                }
              }
              if (matchedMarker) {
                blankNums.push(matchedMarker.number);
              } else {
                blankNums.push('');
              }
            });
          });
        }
      });
      doc.querySelectorAll('table .tiptap-input-field').forEach(span => {
        let node = span;
        let qNum = '';
        while (node && node !== doc.body) {
          let answerWrapper = node.querySelector && node.querySelector('.answer-wrapper');
          if (!answerWrapper && node.classList && node.classList.contains('answer-wrapper')) {
            answerWrapper = node;
          }
          if (answerWrapper && answerWrapper.getAttribute('data-question-number')) {
            qNum = answerWrapper.getAttribute('data-question-number');
            break;
          }
          node = node.parentNode;
        }
        // Question-box in table: get number from preceding marker (<1>, <2>) so bottom nav shows each circle
        if (!qNum && span.closest('.question-box-content')) {
          const parentP = span.closest('p');
          if (parentP) {
            const pHTML = parentP.innerHTML;
            const markers = [];
            const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
            let m;
            while ((m = markerRegex.exec(pHTML)) !== null) {
              markers.push({ number: m[2] || m[3], index: m.index });
            }
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              for (let i = markers.length - 1; i >= 0; i--) {
                if (markers[i].index < spanIndex) {
                  qNum = markers[i].number;
                  break;
                }
              }
            }
          }
        }
        tableBlankNums.push(qNum);
      });
      // Process regular paragraphs for selection fields (not in question-box)
      doc.querySelectorAll('p').forEach(p => {
        if (p.closest('.question-box')) return; // Skip question-box paragraphs, handled separately
        
        const pHTML = p.innerHTML;
        const markers = [];
        const selections = [];
        
        // Find all angle bracket markers with their positions
        const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
        let markerMatch;
        while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
          markers.push({
            number: markerMatch[2] || markerMatch[3],
            index: markerMatch.index
          });
        }
        
        // Find all selection spans
        p.querySelectorAll('.selection-field').forEach(span => {
          if (!span.closest('table') && !span.closest('.question-box')) {
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              selections.push({
                span: span,
                index: spanIndex
              });
            }
          }
        });
        
        // Match each selection to its nearest preceding marker
        selections.forEach(selection => {
          let matchedMarker = null;
          for (let i = markers.length - 1; i >= 0; i--) {
            if (markers[i].index < selection.index) {
              matchedMarker = markers[i];
              break;
            }
          }
          if (matchedMarker) {
            selectionNums.push(matchedMarker.number);
          } else {
            // Fallback: get from parent .question-box
            let box = selection.span.closest('.question-box');
            let boxNum = box ? (box.getAttribute('data-question-number') || box.getAttribute('questionnumber')) : '';
            if (!boxNum && sections[sectionIdx] && sections[sectionIdx].questions && sections[sectionIdx].questions.length > 0) {
              const questionBox = sections[sectionIdx].questions.find(q => q.question_type === 'question-box');
              if (questionBox && questionBox.sample_answer) {
                try {
                  const sampleAnswer = typeof questionBox.sample_answer === 'string' 
                    ? JSON.parse(questionBox.sample_answer) 
                    : questionBox.sample_answer;
                  if (typeof sampleAnswer === 'object' && sampleAnswer !== null) {
                    const qNums = Object.keys(sampleAnswer).filter(k => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
                    if (qNums.length > 0) {
                      const currentDropdownIndex = selectionNums.length;
                      boxNum = qNums[currentDropdownIndex] || qNums[0];
                    }
                  }
                } catch (e) {
                }
              }
            }
            if (boxNum && /^\d+$/.test(boxNum)) {
              selectionNums.push(boxNum);
            }
          }
        });
      });
      
      // Process question-box paragraphs for selection fields
      doc.querySelectorAll('.question-box').forEach(box => {
        const boxContent = box.querySelector('.question-box-content');
        if (boxContent) {
          boxContent.querySelectorAll('p').forEach(p => {
            const pHTML = p.innerHTML;
            const markers = [];
            const selections = [];
            
            // Find all angle bracket markers with their positions
            const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
            let markerMatch;
            while ((markerMatch = markerRegex.exec(pHTML)) !== null) {
              markers.push({
                number: markerMatch[2] || markerMatch[3],
                index: markerMatch.index
              });
            }
            
            // Find all selection spans
            p.querySelectorAll('.selection-field').forEach(span => {
              if (!span.closest('table')) {
                const spanHTML = span.outerHTML;
                const spanIndex = pHTML.indexOf(spanHTML);
                if (spanIndex !== -1) {
                  selections.push({
                    span: span,
                    index: spanIndex
                  });
                }
              }
            });
            
            // Match each selection to its nearest preceding marker
            selections.forEach(selection => {
              let matchedMarker = null;
              for (let i = markers.length - 1; i >= 0; i--) {
                if (markers[i].index < selection.index) {
                  matchedMarker = markers[i];
                  break;
                }
              }
              if (matchedMarker) {
                selectionNums.push(matchedMarker.number);
              }
            });
          });
        }
      });
      doc.querySelectorAll('table .selection-field').forEach(span => {
        let node = span;
        let qNum = '';
        while (node && node !== doc.body) {
          let answerWrapper = node.querySelector && node.querySelector('.answer-wrapper');
          if (!answerWrapper && node.classList && node.classList.contains('answer-wrapper')) {
            answerWrapper = node;
          }
          if (answerWrapper && answerWrapper.getAttribute('data-question-number')) {
            qNum = answerWrapper.getAttribute('data-question-number');
            break;
          }
          node = node.parentNode;
        }
        // Question-box in table: get number from preceding marker (<3>, <4>) so bottom nav shows dropdown circles
        if (!qNum && span.closest('.question-box-content')) {
          const parentP = span.closest('p');
          if (parentP) {
            const pHTML = parentP.innerHTML;
            const markers = [];
            const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
            let m;
            while ((m = markerRegex.exec(pHTML)) !== null) {
              markers.push({ number: m[2] || m[3], index: m.index });
            }
            const spanHTML = span.outerHTML;
            const spanIndex = pHTML.indexOf(spanHTML);
            if (spanIndex !== -1) {
              for (let i = markers.length - 1; i >= 0; i--) {
                if (markers[i].index < spanIndex) {
                  qNum = markers[i].number;
                  break;
                }
              }
            }
          }
        }
        tableSelectionNums.push(qNum);
      });
      
      // Extract question numbers from MCQ containers
      doc.querySelectorAll('.mcq-container').forEach(mcqContainer => {
        const mcqType = mcqContainer.getAttribute('data-mcq-type') || 'single';
        const questionRange = mcqContainer.getAttribute('data-question-range') || '';
        
        if (mcqType === 'single') {
          // Preferred: explicit number on the container
          let qNum = mcqContainer.getAttribute('data-question-number') || '';
          
          // Backward compatibility: extract from question text (pattern: "1. ..." or "1) ...")
          if (!qNum || !/^\d+$/.test(qNum)) {
            const questionText = mcqContainer.querySelector('.mcq-question-text')?.textContent || '';
            const qtMatch = questionText.match(/^(\d+)\s*[\.\)]\s*/);
            if (qtMatch && qtMatch[1]) {
              qNum = qtMatch[1];
            }
          }
          
          // Fallback: try to find question number from preceding content
          if (!qNum || !/^\d+$/.test(qNum)) {
            let prevSibling = mcqContainer.previousElementSibling;
            while (prevSibling && !qNum) {
              if (prevSibling.tagName === 'P') {
                const numberMatch = prevSibling.innerHTML.match(/(&lt;(\d+)&gt;|<(\d+)>)/);
                if (numberMatch) {
                  qNum = numberMatch[2] || numberMatch[3];
                  break;
                }
              }
              prevSibling = prevSibling.previousElementSibling;
            }
          }
          
          if (qNum && /^\d+$/.test(qNum)) {
            mcqNums.push(qNum);
          }
        } else if (mcqType === 'multiple' && questionRange) {
          // For multiple-answer MCQ, expand the range
          const expandedNums = expandQuestionRange(questionRange);
          mcqNums.push(...expandedNums);
        }
      });

      doc.querySelectorAll('.drag-drop-field').forEach((field, idx) => {
        let qNum = field.getAttribute('data-question-number') || '';
        if (!qNum || !/^\d+$/.test(qNum)) {
          const parent = field.parentElement;
          const parentHtml = parent?.innerHTML || '';
          const fieldIndex = parentHtml.indexOf(field.outerHTML);
          const markers = [];
          const markerRegex = /(&lt;(\d+)&gt;|<(\d+)>)/g;
          let markerMatch;
          while ((markerMatch = markerRegex.exec(parentHtml)) !== null) {
            markers.push({ number: markerMatch[2] || markerMatch[3], index: markerMatch.index });
          }
          for (let markerIndex = markers.length - 1; markerIndex >= 0; markerIndex--) {
            if (markers[markerIndex].index < fieldIndex) {
              qNum = markers[markerIndex].number;
              break;
            }
          }
        }
        if (qNum && /^\d+$/.test(qNum)) {
          dragDropNums.push(qNum);
        }
      });
      const dropMarkerText = doc.body?.textContent || '';
      const dropMarkerRegex = /\[Drop here for Q\s*(\d+)\]/gi;
      let dropMarkerMatch;
      while ((dropMarkerMatch = dropMarkerRegex.exec(dropMarkerText)) !== null) {
        if (dropMarkerMatch[1]) {
          dragDropNums.push(dropMarkerMatch[1]);
        }
      }
      
      const allNumsFromHtml = [...blankNums, ...tableBlankNums, ...selectionNums, ...tableSelectionNums, ...mcqNums, ...dragDropNums].filter(n => n && /^\d+$/.test(n));
      const questionBox = sections[sectionIdx].questions && sections[sectionIdx].questions.find(q => q.question_type === 'question-box');
      
      const blankCount = doc.querySelectorAll('.tiptap-input-field').length;
      const dropdownCount = doc.querySelectorAll('.selection-field').length;
      const dragDropCount = doc.querySelectorAll('.drag-drop-field').length;
      const totalInteractiveCount = blankCount + dropdownCount + dragDropCount;
      
      if (totalInteractiveCount > 0 && allNumsFromHtml.length < totalInteractiveCount && questionBox && questionBox.sample_answer) {
        try {
          const sampleAnswer = typeof questionBox.sample_answer === 'string' 
            ? JSON.parse(questionBox.sample_answer) 
            : questionBox.sample_answer;
          if (typeof sampleAnswer === 'object' && sampleAnswer !== null) {
            const qNumsFromSample = Object.keys(sampleAnswer).filter(k => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
            
            const blanksNeedNumbers = blankCount > 0 && blankNums.filter(n => n && /^\d+$/.test(n)).length < blankCount;
            const dropdownsNeedNumbers = dropdownCount > 0 && selectionNums.filter(n => n && /^\d+$/.test(n)).length < dropdownCount;
            
            let qNumIdx = 0;
            if (blanksNeedNumbers) {
              const currentBlanks = blankNums.filter(n => n && /^\d+$/.test(n)).length;
              for (let i = currentBlanks; i < blankCount && qNumIdx < qNumsFromSample.length; i++) {
                if (blankNums[i]) {
                  blankNums[i] = qNumsFromSample[qNumIdx++];
                } else {
                  blankNums.push(qNumsFromSample[qNumIdx++]);
                }
              }
            }
            if (dropdownsNeedNumbers) {
              const currentDropdowns = selectionNums.filter(n => n && /^\d+$/.test(n)).length;
              for (let i = currentDropdowns; i < dropdownCount && qNumIdx < qNumsFromSample.length; i++) {
                if (selectionNums[i]) {
                  selectionNums[i] = qNumsFromSample[qNumIdx++];
                } else {
                  selectionNums.push(qNumsFromSample[qNumIdx++]);
                }
              }
            }
          }
        } catch (e) {
        }
      }
    }
    let allNums = [...blankNums, ...tableBlankNums, ...selectionNums, ...tableSelectionNums, ...mcqNums, ...dragDropNums];
    const seen = new Set();
    allNums = allNums.filter(num => {
      if (!num || !/^\d+$/.test(num) || seen.has(num)) return false;
      seen.add(num);
      return true;
    });
    
    if (allNums.length === 0 && sections[sectionIdx]) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`${sections[sectionIdx].passage_html || ''}${sections[sectionIdx].passage_text || ''}`, 'text/html');
      const totalInteractiveCount = doc.querySelectorAll('.tiptap-input-field, .selection-field, .drag-drop-field').length;
      if (totalInteractiveCount > 0) {
        const answerSheetObj = parseAnswerSheet(answer_sheet || '{}');
        const allQNumsFromSheet = Object.keys(answerSheetObj).filter(k => /^\d+$/.test(k)).sort((a, b) => Number(a) - Number(b));
        if (allQNumsFromSheet.length > 0) {
          const questionsPerSection = Math.ceil(allQNumsFromSheet.length / (sections.length || 1));
          const startIdx = sectionIdx * questionsPerSection;
          const endIdx = Math.min(startIdx + questionsPerSection, allQNumsFromSheet.length);
          allNums = allQNumsFromSheet.slice(startIdx, endIdx).slice(0, totalInteractiveCount);
        }
      }
    }
    
    return allNums.sort((a, b) => Number(a) - Number(b));
  }

  function getCurrentSectionQuestionNumbers() {
    return getSectionQuestionNumbers(currentPart - 1);
  }

  // Sync showRedForUnanswered with showSkipWarning
  useEffect(() => {
    setShowRedForUnanswered(showSkipWarning);
  }, [showSkipWarning]);

  // Clear skippedQuestions when showRedForUnanswered becomes false
  useEffect(() => {
    if (!showRedForUnanswered) {
      setSkippedQuestions([]);
    }
  }, [showRedForUnanswered]);

  // Helper to check if a question is incorrect (for results mode - when test is submitted)
  // Also shows unanswered questions as red when warning modal is displayed
  const isQuestionIncorrect = useCallback((sectionIdx, questionNumber) => {
    // If test has been submitted, use analytics from backend
    if (apiResult && apiResult.response && apiResult.response.analytics) {
      const analytics = apiResult.response.analytics[questionNumber];
      return analytics && analytics.isCorrect === false;
    }
    
    // If warning modal is shown, show red circles for unanswered questions
    if (showRedForUnanswered) {
      const isAnswered = allAnswers[questionNumber] !== undefined && 
                        allAnswers[questionNumber] !== '' && 
                        allAnswers[questionNumber] !== null;
      return !isAnswered; // Return true if unanswered (will show red)
    }
    
    // Otherwise, don't show red circles
    return false;
  }, [apiResult, showRedForUnanswered, allAnswers]);

  const partNumbers = selectedSectionIndices
    ? selectedSectionIndices.map(i => i + 1)
    : sections.map((section, idx) => section.part || idx + 1);
  const totalRenderedQuestions = new Set(
    sections.flatMap((_, sectionIndex) => getSectionQuestionNumbers(sectionIndex))
  ).size;

  const content = (
    <>
      <Global styles={ieltsPassageGlobalStyles} />
      <Global styles={ieltsReadingPassageStyles} />
      <TestContainer sx={{ 
        width: '100%', 
        margin: '0 auto', 
        boxSizing: 'border-box', 
        height: isReviewMode ? 'auto' : '100vh', 
        minHeight: isReviewMode ? 'unset' : '100vh',
        overflow: isReviewMode ? 'visible' : 'hidden' 
      }}>
        {/* Conditionally render header or band score */}
      {!isReviewMode && (!isTestMode ? (
        <IELTSHeader
          isSimulationMode={isSimulationMode}
          timerText={formatTime(timer.minutes === 'Unlimited' ? 'Unlimited' : timer.minutes * 60 + timer.seconds) + ' remaining'}
          showReview={false}
          showSaveExit={showSaveExit && !apiResult}
          showSubmit={true}
          apiResult={apiResult}
          groupId={groupId}
          onReview={() => {}}
          onSaveExit={handleSaveAndExit}
          onSubmit={handleSubmitWithWarning}
          onSubmitNavigate="/ielts-results"
          onFullTestPause={onFullTestPause}
        />
      ) : (
        <IELTSBandScore apiResult={apiResult} showCorrectIncorrect={true} totalQuestions={totalRenderedQuestions} />
      ))}

      {/* Bottom Navigation - above content for review mode (results page) */}
      {isReviewMode && (
        <Box sx={{ width: '100%', mb: 2, '& > .MuiBox-root': { pt: 0, mt: 0 }, '& .MuiContainer-root': { maxWidth: 'none', px: 0 } }}>
          <IELTSBottomNavigation
            key={`nav-review-${apiResult ? 'submitted' : 'active'}`}
            parts={partNumbers.map((part, idx) => ({
              part,
              sectionIdx: selectedSectionIndices ? selectedSectionIndices[idx] : idx,
              part_name: sections[idx]?.part_name || `Part ${part}`
            }))}
            currentPart={currentPart}
            onPartChange={handlePartChange}
            getQuestionNumbers={getSectionQuestionNumbers}
            allAnswers={allAnswers}
            isQuestionIncorrect={isQuestionIncorrect}
            skippedQuestions={skippedQuestions}
            onQuestionClick={handleQuestionSelect}
            getPartName={(part) => part.part_name || `Part ${part.part || part}`}
            sections={sections}
            fixed={false}
          />
        </Box>
      )}

      {/* Draggable Split Content */}
      <Box ref={containerRef} sx={{ 
        display: 'flex', 
        gap: 0, 
        width: '100%',
        height: isReviewMode ? '600px' : '100%', 
        minHeight: isReviewMode ? '600px' : 0, 
        maxHeight: isReviewMode ? '600px' : 'unset',
        marginBottom: !isTestMode ? '77px' : 0,
        position: 'relative', 
        overflow: 'hidden',
        alignItems: 'stretch'
      }}>
        {/* Left Side - Reading Passage */}
        <Box sx={{ width: `${leftWidth}%`, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <PassageContainer sx={{ 
            ...(isReviewMode && { border: '1px solid #E6E8EE', borderRadius: '8px', boxShadow: 'none', boxSizing: 'border-box' }),
            height: '100%', 
            minHeight: 0, 
            margin: 0,
            overflowY: 'auto',
            paddingBottom: !isTestMode ? '77px' : '20px'
          }} onScroll={() => setReadingSelection(null)}>
            <style>{ieltsPassageInlineStyles}</style>
            <style>{ieltsReadingPassageInlineStyles}</style>
            <div
              ref={passageRef}
              className="ielts-passage-html"
              tabIndex={0}
              onMouseUp={captureReadingSelection}
              onTouchEnd={captureReadingSelection}
              onKeyUp={captureReadingSelection}
              onClick={handlePassageClick}
              onKeyDown={handlePassageKeyDown}
              dangerouslySetInnerHTML={{ __html: annotatedPassageHtml }}
            />
          </PassageContainer>
        </Box>
        {readingSelection && (
          <Box sx={{
            position: 'fixed',
            zIndex: 1300,
            left: Math.max(8, Math.min(readingSelection.rect.left, window.innerWidth - 112)),
            top: Math.max(8, readingSelection.rect.bottom + 8),
            display: 'flex',
            gap: 1,
            p: 1,
            bgcolor: '#fffdf5',
            border: '1px solid #d8d2c0',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.16)',
          }}>
            <Tooltip title="Highlight selected text">
              <IconButton
                aria-label="Highlight selected text"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applyReadingSelection('marker')}
                sx={{ bgcolor: '#996CFE', color: '#fff', '&:hover': { bgcolor: '#7C4DFF' } }}
              >
                <BorderColorIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add note to selected text">
              <IconButton
                aria-label="Add note to selected text"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => applyReadingSelection('note')}
                sx={{ bgcolor: '#996CFE', color: '#fff', '&:hover': { bgcolor: '#7C4DFF' } }}
              >
                <NoteAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Draggable Divider */}
        <DraggableDivider dragging={dragging} onMouseDown={handleMouseDown} />
        {/* Right Side - Questions (min width enforced by LEFT_WIDTH_MAX in resize handler) */}
        <Box sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {/* Loading overlay with fade effect */}
          {isSubmitting && (
            <Box sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              bgcolor: 'rgba(30, 16, 60, 0.25)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.3s',
            }}>
              <CircularProgress size={64} sx={{ color: '#996CFE' }} />
            </Box>
          )}
          <QuestionsContainer sx={{ 
            ...(isReviewMode && { border: '1px solid #E6E8EE', borderRadius: '8px', boxShadow: 'none', boxSizing: 'border-box' }),
            height: '100%', 
            minHeight: 0, 
            margin: 0,
            overflowY: 'auto',
            paddingBottom: !isTestMode ? '77px' : '20px'
          }}>
            {/* Result Summary Modal/Box (now inside QuestionsContainer, above questions) */}
            {/* Band card should only show in practice/simulation mode, not on results page (review mode) */}
            {!isTestMode && !isReviewMode && (
              <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', mt: 2, mb: 3 }}>
                <IELTSBandScore 
                  apiResult={apiResult} 
                  showCorrectIncorrect={true}
                  totalQuestions={totalRenderedQuestions}
                />
              </Box>
            )}
            {/* Table, headings, and paragraph styling for passage_text */}
            <style>{ieltsPassageInlineStyles}</style>
            <style>{ieltsReadingPassageInlineStyles}</style>
            <div
              className="ielts-passage-questions"
              dangerouslySetInnerHTML={{ __html: cleanedPassageText }}
            />
          </QuestionsContainer>
        </Box>
      </Box>
      {/* Bottom Navigation - at bottom for test mode and practice/simulation modes only (not review mode) */}
      {!isReviewMode && (
        <IELTSBottomNavigation
          key={`nav-${showRedForUnanswered ? 'warning' : 'normal'}-${apiResult ? 'submitted' : 'active'}`}
          parts={partNumbers.map((part, idx) => ({
            part,
            sectionIdx: selectedSectionIndices ? selectedSectionIndices[idx] : idx,
            part_name: sections[idx]?.part_name || `Part ${part}`
          }))}
          currentPart={currentPart}
          onPartChange={handlePartChange}
          getQuestionNumbers={getSectionQuestionNumbers}
          allAnswers={allAnswers}
          isQuestionIncorrect={isQuestionIncorrect}
          skippedQuestions={skippedQuestions}
          onQuestionClick={handleQuestionSelect}
          getPartName={(part) => part.part_name || `Part ${part.part || part}`}
          sections={sections}
          fixed={!isTestMode}
        />
      )}

      <Dialog
        open={Boolean(activeReadingAnnotation)}
        onClose={() => {
          setActiveReadingAnnotation(null);
          setReadingNoteDraft('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {activeReadingAnnotation?.type === 'note' ? 'Reading note' : 'Highlighted text'}
        </DialogTitle>
        <DialogContent>
          {activeReadingAnnotation?.type === 'note' ? (
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              value={readingNoteDraft}
              onChange={(event) => setReadingNoteDraft(event.target.value.slice(0, 1000))}
              placeholder="Write a note for this highlight"
              inputProps={{ maxLength: 1000 }}
              sx={{ mt: 1 }}
            />
          ) : (
            <Typography color="text.secondary">
              Remove this highlight from the passage?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={removeActiveReadingAnnotation}>Remove</Button>
          <Button onClick={() => {
            setActiveReadingAnnotation(null);
            setReadingNoteDraft('');
          }}>Cancel</Button>
          {activeReadingAnnotation?.type === 'note' && (
            <Button variant="contained" onClick={saveReadingNote}>Save</Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(officialExplanation)}
        onClose={() => setOfficialExplanation('')}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Answer explanation</DialogTitle>
        <DialogContent>
          <Box dangerouslySetInnerHTML={{ __html: officialExplanation }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfficialExplanation('')}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Skip Warning Dialog */}
      <Dialog
        open={showSkipWarning}
        onClose={() => {
          setShowSkipWarning(false);
          setShowRedForUnanswered(false);
          setSkippedQuestions([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          color: '#FF5252', 
          fontWeight: 600,
          fontSize: '20px',
          pb: 1
        }}>
          ⚠️ Unattempted Questions
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ textAlign: 'center', mb: 2 }}>
            You have some unattempted questions. Are you sure you want to submit?
          </Typography>
          
          <Box sx={{ backgroundColor: '#FFF3F3', p: 2, borderRadius: '8px', border: '1px solid #FF5252' }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              These questions will be marked as incorrect if you submit now.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={() => {
              setShowSkipWarning(false);
              setShowRedForUnanswered(false);
              setSkippedQuestions([]);
            }}
            variant="outlined"
            sx={{ 
              borderColor: '#E0E0E0',
              color: '#666',
              '&:hover': { 
                borderColor: '#996CFE',
                backgroundColor: '#F5F0FF'
              }
            }}
          >
            Go Back
          </Button>
          <Button
            onClick={() => {
              setShowSkipWarning(false);
              handleApiSubmit();
            }}
            variant="contained"
            sx={{ 
              backgroundColor: '#FF5252',
              '&:hover': { backgroundColor: '#E53935' },
              px: 3
            }}
          >
            Submit Anyway
          </Button>
        </DialogActions>
      </Dialog>
      </TestContainer>
    </>
  );
  return isReviewMode ? (
    <ReadingResultsFrame apiResult={apiResult} totalQuestions={totalRenderedQuestions} duration={resultDuration} durationLabel={resultDurationLabel} teacherReviewContent={teacherReviewContent}>
      {content}
    </ReadingResultsFrame>
  ) : content;
};

export default ReadingTestUI;
